/**
 * Handles radios and checkboxes change event to toggle a class to a given element
 * @module toggleChange
 */

import $ from 'jquery';
import registerJQueryPlugin from '../lib/register-jquery-plugin';
import mergeOptions from '../lib/merge-options';
export const PLUGIN_NAME = 'toggleChange';
export const DEFAULTS = {
  ctToggleClass: '',
  ctSelector: '',
  ctInverse: false,
  ctChildSelector: [],
  ctChildToggleClass: [],
  ctChildInverse: [],
};


/**
 * Normalizes an option into an array so callers can safely iterate it.
 * jQuery's .data() only parses a data attribute into an array when it is
 * valid JSON (double quotes); a single-quoted value comes through as a raw
 * string. This accepts a real array, a bracketed string (single or double
 * quotes), or a plain string, and always returns an array.
 * @param  {Array|String} value - option value from data attributes / init
 * @return {Array} normalized array (empty when nothing usable is provided)
 */
const toArray = (value) => {
  if (Array.isArray(value)) {
    return value;
  }
  if (typeof value !== 'string' || value.trim() === '') {
    return [];
  }
  const trimmed = value.trim();
  if (trimmed.charAt(0) === '[' && trimmed.charAt(trimmed.length - 1) === ']') {
    try {
      const parsed = JSON.parse(trimmed.replace(/'/g, '"'));
      return Array.isArray(parsed) ? parsed : [parsed];
    } catch (e) {
      return trimmed
        .slice(1, -1)
        .split(',')
        .map(item => item.trim().replace(/^['"]+|['"]+$/g, ''))
        .filter(Boolean);
    }
  }
  return [trimmed];
};

/**
 * Return the input type of the element
 * @param  {jQuery} $el - checkbox / radio
 * @return {String} type of either checkbox or radio
 */
const inputType = $el => $el.attr('type');

/**
 * Return radio group
 * @param  {jQuery} $el - checkbox / radio
 * @return {jQuery} radio group
 */
const radioGroup = ($el) => {
  const groupName = $el.attr('name');
  return $(`input[type="radio"][name="${groupName}"]`);
};

const toggleClassHandler = ($el, action, opts) => {
  if( action == "add" && !opts.ctInverse || action == "remove" && opts.ctInverse ) {
    $el.addClass(opts.ctToggleClass);
  }
  if( action == "remove" && !opts.ctInverse || action == "add" && opts.ctInverse ) {
    $el.removeClass(opts.ctToggleClass);
  }
}
 const toggleChildClassHandler = ($el, action, opts) => {
  opts.ctChildSelector.forEach((childSelector, index) => {
    const childToggleClass = opts.ctChildToggleClass[index];
    $el.find(childSelector).each(function toggleFoundChild() {
      const $child = $(this);
      if( action == "add" && !opts.ctChildInverse[index] || action == "remove" && opts.ctChildInverse[index] ) {
        $child.addClass(childToggleClass);
      }
      if( action == "remove" && !opts.ctChildInverse[index] || action == "add" && opts.ctChildInverse[index] ) {
        $child.removeClass(childToggleClass);
      }
    });
  });
}

/**
 * Applies any sort of event handlers for the checkboxes or radios.
 * @param  {jQuery} $el - checkbox / radio
 * @param  {Object} [options={}] - Any options provided upon initialization
 */
const setEventListeners = ($el, options = {}) => {
  const $selector = $(options.ctSelector);
  const type = inputType($el);
  if (type === 'radio') {
    const $radioGroup = radioGroup($el);
    const deselectEvent = `deselect-${$el.attr('name')}`;
    $radioGroup.on('click', function customDeselectEvent() {
      $(`input[name="${$(this).attr('name')}"]`).not($(this)).trigger(deselectEvent);
    });
    $el
      .on('change', () => {
      toggleClassHandler($selector, "add", options);
  })
  .on(deselectEvent, () => {
      toggleClassHandler($selector, "remove", options);
  });
  }
  if (type === 'checkbox') {
    $el.on('change', (evt) => {
      if (evt.currentTarget.checked) {
      toggleClassHandler($selector, "add", options);
      if (options.ctChildSelector.length > 0 && options.ctChildToggleClass.length > 0) {
        toggleChildClassHandler($selector, "add", options);
      }
    }
    if (!evt.currentTarget.checked) {
      toggleClassHandler($selector, "remove", options);
      if (options.ctChildSelector.length > 0 && options.ctChildToggleClass.length > 0) {
        toggleChildClassHandler($selector, "remove", options);
      }
    }
  });
  }
};
/**
 * Set dom state if element is checked
 * @param  {jQuery} $el - checkbox / radio
 * @param  {Object} [options={}] - Any options provided upon initialization
 */
const setChecked = ($el, options = {}) => {
  const $selector = $(options.ctSelector);
  if ($el[0].checked && !$el[0].disabled && !options.ctInverse) {
    $selector.addClass(options.ctToggleClass);
    if (options.ctChildSelector.length > 0 && options.ctChildToggleClass.length > 0) {
      toggleChildClassHandler($selector, "add", options);
    }
  }
};
/**
 * Initializes toggleChange.
 * @param {HTMLElement} el - button /element that triggers toggleChange
 * @param {Object} options - Options used for wiring up the toggleChange
 * @param {Object} deregister - Performs a deregisteration on the plugin
 * @see DEFAULTS
 */


export default function toggleChange(el, options = {}) {
  const $el = $(el);
  const opts = mergeOptions(DEFAULTS, options, $el);
  opts.ctChildSelector = toArray(opts.ctChildSelector);
  opts.ctChildToggleClass = toArray(opts.ctChildToggleClass);
  opts.ctChildInverse = toArray(opts.ctChildInverse);
  setEventListeners($el, opts);
  setChecked($el, opts);
}
registerJQueryPlugin(PLUGIN_NAME, toggleChange);



// WEBPACK FOOTER //
// ./styleguide/src/assets/modules/toggle-change.js